import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  Button, PageShell, SectionCard, FormSection, Input, RadixSelect, Switch, RichTextEditor, Skeleton,
  ImageUploadField, RequiredMark,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { useImageUploadQueue } from '@/lib/upload';
import { useEverTvVideoQuery, useEverTvVideoMutation } from '../hooks/useEverTvVideosQuery';
import { useEverTvImageUpload } from '../hooks/useEverTvImageUpload';
import { extractYoutubeId } from '../lib/youtubeId';
import {
  EVER_TV_CATEGORIES,
  type EverTvCategory,
  type EverTvVideoPayload,
} from '../types/everTvVideo';
import { editorImageGuideFor } from '@/lib/upload/uploadGuide';

interface EverTvFormState {
  videoId: string;
  title: string;
  content: string;
  thumbnailUrl: string;
  youtubeId: string;
  artistName: string;
  duration: string;
  category: EverTvCategory | '';
  sortOrder: string;
  isActive: boolean;
  publishedAt: string;
}

const EMPTY_FORM: EverTvFormState = {
  videoId: '',
  title: '',
  content: '',
  thumbnailUrl: '',
  youtubeId: '',
  artistName: '',
  duration: '',
  category: '',
  sortOrder: '0',
  isActive: true,
  publishedAt: '',
};

const SLUG_PATTERN = /^[a-z0-9-]+$/;

/** wire publishedAt ("YYYY.MM.DD" 또는 ISO) → value for <input type="date"> ("YYYY-MM-DD"). */
const wireDateToInput = (wire: string | null): string => {
  if (!wire) return '';
  const m = /^(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})/.exec(wire.trim());
  if (!m) return '';
  const [, year, month, day] = m;
  const pad = (s: string | undefined) => (s ?? '').padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
};

/** <input type="date"> value ("YYYY-MM-DD") → wire publishedAt ("YYYY.MM.DD"). */
const inputDateToWire = (input: string): string | undefined => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input.trim());
  return m ? `${m[1]}.${m[2]}.${m[3]}` : undefined;
};

export const EverTvFormPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { videoId: videoIdParam } = useParams<{ videoId: string }>();
  const isNew = videoIdParam === 'new' || videoIdParam == null;
  const videoId = isNew ? null : videoIdParam ?? null;

  const query = useEverTvVideoQuery(videoId);
  const { create, update, remove } = useEverTvVideoMutation();

  const [form, setForm] = useState<EverTvFormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const uploads = useImageUploadQueue();
  const uploadBodyImage = useEverTvImageUpload();

  useEffect(() => {
    if (!query.data) return;
    setForm({
      videoId: query.data.videoId,
      title: query.data.title,
      content: query.data.content ?? '',
      thumbnailUrl: query.data.thumbnailUrl,
      youtubeId: query.data.youtubeId ?? '',
      artistName: query.data.artistName ?? '',
      duration: query.data.duration ?? '',
      category: query.data.category ?? '',
      sortOrder: String(query.data.sortOrder ?? 0),
      isActive: query.data.isActive,
      publishedAt: wireDateToInput(query.data.publishedAt),
    });
  }, [query.data]);

  const set = <K extends keyof EverTvFormState>(key: K, value: EverTvFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const categoryOptions = [
    { value: '', label: t('everTv.fields.categoryNone') },
    ...EVER_TV_CATEGORIES.map((c) => ({ value: c, label: t(`everTv.category.${c}`) })),
  ];

  const slug = form.videoId.trim();
  // 썸네일은 "저장 시 업로드" 모델: 선택 즉시 form.thumbnailUrl 에 반영되지 않고
  // flush() 시점에 publicUrl 로 치환된다. 따라서 제출 가능 판정에는 이미 저장된 URL
  // (form.thumbnailUrl) 또는 업로드 대기 중인 로컬 파일(uploads.hasPending) 둘 중
  // 하나만 있으면 된다.
  const hasThumbnail = form.thumbnailUrl.trim() !== '' || uploads.hasPending;
  const canSubmit =
    slug !== '' &&
    form.title.trim() !== '' &&
    form.content.trim() !== '' &&
    hasThumbnail;

  const handleSubmit = async () => {
    setError(null);
    if (!canSubmit) {
      setError(t('everTv.validation.required'));
      return;
    }
    if (isNew && !SLUG_PATTERN.test(slug)) {
      setError(t('everTv.validation.slug'));
      return;
    }
    try {
      const resolved = await uploads.flush();
      const thumbnailUrl =
        typeof resolved.thumbnailUrl === 'string'
          ? resolved.thumbnailUrl
          : form.thumbnailUrl.trim();
      if (thumbnailUrl === '') {
        setError(t('everTv.validation.required'));
        return;
      }
      const publishedAtWire = inputDateToWire(form.publishedAt);
      const parsedSortOrder = Number.parseInt(form.sortOrder, 10);
      const youtubeId = extractYoutubeId(form.youtubeId);
      const artistName = form.artistName.trim();
      const duration = form.duration.trim();
      const payload: EverTvVideoPayload = {
        videoId: slug,
        title: form.title.trim(),
        content: form.content,
        thumbnailUrl,
        sortOrder: Number.isFinite(parsedSortOrder) ? parsedSortOrder : 0,
        isActive: form.isActive,
        ...(form.category ? { category: form.category } : {}),
        ...(publishedAtWire ? { publishedAt: publishedAtWire } : {}),
        ...(youtubeId ? { youtubeId } : {}),
        ...(artistName ? { artistName } : {}),
        ...(duration ? { duration } : {}),
      };
      if (isNew) {
        await create.mutateAsync(payload);
      } else if (videoId != null) {
        await update.mutateAsync({ videoId, payload });
      }
      navigate('/cms/ever-tv');
    } catch (e) {
      setError(friendlyMessage(e, t('everTv.saveFailed')));
    }
  };

  const handleDelete = async () => {
    if (videoId == null) return;
    setError(null);
    try {
      await remove.mutateAsync(videoId);
      navigate('/cms/ever-tv');
    } catch (e) {
      setError(friendlyMessage(e, t('everTv.deleteFailed')));
    }
  };

  const isBusy = create.isPending || update.isPending || remove.isPending || uploads.isUploading;

  const breadcrumb = [
    { label: 'CMS', href: '/cms/ever-tv' },
    { label: t('everTv.title'), href: '/cms/ever-tv' },
    { label: isNew ? t('everTv.create') : t('everTv.edit') },
  ];

  if (!isNew && query.isPending) {
    return (
      <PageShell title={t('everTv.editTitle')} breadcrumb={breadcrumb}>
        <div className="space-y-3" role="status" aria-live="polite">
          <Skeleton className="h-[44px] w-full" />
          <Skeleton className="h-[200px] w-full" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={isNew ? t('everTv.createTitle') : t('everTv.editTitle')}
      breadcrumb={breadcrumb}
      actions={
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={isBusy}>
              {t('everTv.delete')}
            </Button>
          )}
          <Button size="sm" onClick={handleSubmit} disabled={isBusy || !canSubmit}>
            {uploads.isUploading ? t('everTv.uploading', { defaultValue: '이미지 업로드 중…' }) : t('everTv.save')}
          </Button>
        </div>
      }
    >
      {(error || uploads.error) && (
        <div className="rounded-md p-3 text-sm" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
          {error ?? uploads.error}
        </div>
      )}

      <SectionCard title={t('everTv.sectionBasic')}>
        <div className="flex flex-col gap-3">
          <FormSection label={t('everTv.fields.videoId')} density="compact" required>
            <Input
              value={form.videoId}
              onChange={(e) => set('videoId', e.target.value)}
              maxLength={64}
              disabled={!isNew}
              placeholder={t('everTv.fields.videoIdPlaceholder')}
              className="max-w-[360px]"
            />
          </FormSection>
          <FormSection label={t('everTv.fields.title')} density="compact" required>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={200} required />
          </FormSection>
          <FormSection label={t('everTv.fields.category')} density="compact">
            <div className="max-w-[200px]">
              <RadixSelect
                value={form.category}
                onChange={(v) => set('category', v as EverTvCategory | '')}
                options={categoryOptions}
                aria-label={t('everTv.fields.category')}
              />
            </div>
          </FormSection>
          <FormSection label={t('everTv.fields.sortOrder')} density="compact">
            <Input
              type="number"
              value={form.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
              className="max-w-[140px]"
            />
          </FormSection>
          <FormSection label={t('everTv.fields.publishedAt')} density="compact">
            <Input
              type="date"
              value={form.publishedAt}
              onChange={(e) => set('publishedAt', e.target.value)}
              className="max-w-[200px]"
            />
          </FormSection>
          <FormSection label={t('everTv.fields.isActive')} density="compact">
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => set('isActive', v)}
              aria-label={t('everTv.fields.isActive')}
            />
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard title={t('everTv.sectionMedia')}>
        <FormSection label={t('everTv.fields.thumbnail', { defaultValue: '썸네일' })} density="compact" required>
          <ImageUploadField
            queue={uploads}
            name="thumbnailUrl"
            category="MISC"
            // QA #57 — MISC 카테고리는 공용이라 권장 규격 매핑이 없다. 스토어프론트 EVER TV 목록이
            // 썸네일을 16:9 박스에 object-fit: cover 로 채우므로(ever-tv/page.styled.tsx ThumbWrap),
            // 다른 비율은 잘린다 — 그 근거로 여기서만 명시한다.
            guide={t('everTv.fields.thumbnailGuide', {
              defaultValue: '권장 1600×900px · 16:9 — 쇼핑몰 목록에서 16:9 로 잘려 노출됩니다',
            })}
            value={form.thumbnailUrl || null}
            onChange={(url) => set('thumbnailUrl', url ?? '')}
            disabled={isBusy}
          />
        </FormSection>
      </SectionCard>

      <SectionCard title={t('everTv.sectionVideo', { defaultValue: '영상' })}>
        <div className="flex flex-col gap-3">
          <FormSection
            label={t('everTv.fields.youtubeId', { defaultValue: 'YouTube ID' })}
            description={t('everTv.fields.youtubeIdHint', {
              defaultValue: 'YouTube 영상 ID 또는 공유/시청 URL을 입력하면 영상 ID만 저장됩니다.',
            })}
            density="compact"
          >
            <Input
              value={form.youtubeId}
              onChange={(e) => set('youtubeId', e.target.value)}
              maxLength={128}
              placeholder={t('everTv.fields.youtubeIdPlaceholder', { defaultValue: '예: dQw4w9WgXcQ' })}
              className="max-w-[360px]"
            />
          </FormSection>
          <FormSection label={t('everTv.fields.artistName', { defaultValue: '아티스트' })} density="compact">
            <Input
              value={form.artistName}
              onChange={(e) => set('artistName', e.target.value)}
              maxLength={100}
              className="max-w-[280px]"
            />
          </FormSection>
          <FormSection label={t('everTv.fields.duration', { defaultValue: '재생 시간' })} density="compact">
            <Input
              value={form.duration}
              onChange={(e) => set('duration', e.target.value)}
              maxLength={16}
              placeholder={t('everTv.fields.durationPlaceholder', { defaultValue: '예: 03:24' })}
              className="max-w-[140px]"
            />
          </FormSection>
        </div>
      </SectionCard>

      {/* QA-56 — 본문은 저장 검증(canSubmit)이 하드 강제하므로 필수 표기. */}
      <SectionCard title={<span>{t('everTv.sectionContent', { defaultValue: '본문' })}<RequiredMark /></span>}>
        <RichTextEditor
          value={form.content}
          onChange={(html) => set('content', html)}
          placeholder={t('everTv.fields.contentPlaceholder', { defaultValue: '게시글 본문을 입력하세요.' })}
          aria-label={t('everTv.fields.content', { defaultValue: '본문' })}
          onUploadImage={uploadBodyImage}
          imageGuide={editorImageGuideFor('EVER_TV_BODY')}
          onUploadError={(e) => setError(friendlyMessage(e, t('everTv.imageUploadFailed', { defaultValue: '이미지 업로드에 실패했습니다.' })))}
        />
      </SectionCard>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('everTv.deleteTitle')}</DialogTitle>
            <DialogDescription>{t('everTv.deleteDesc', { title: form.title })}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('everTv.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={isBusy}
              onClick={() => { setConfirmDelete(false); void handleDelete(); }}
            >
              {t('everTv.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
